import { Container, Graphics, InteractionEvent, Loader, Sprite, Text, VideoResource } from 'pixi.js';
import { Game, Scene, Store, TextureFactory } from '../core';
import { StarCutAway } from '../cutaway';
import { Point } from '../interface';
import { FinalGameScene } from '.';
import { Cut } from '../class/opening/index';
import { CostumeItem } from '../class';
import { sound } from '@pixi/sound';
import { LOCALE_DATA } from '../constant';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';

class EquipmentsInventory extends Container {
  private costumeItems: Array<CostumeItem> = [];

  constructor() {
    super();

    let selectedCostume = 0;
    const bgTexture = TextureFactory.instance.getSingleTexture('/textures/ui/background_effect.png');
    const bgSprite = new Sprite(bgTexture);
    this.addChild(bgSprite);

    const texture = TextureFactory.instance.getSingleTexture('/textures/ui/equipments.png');
    const sprite = new Sprite(texture);
    sprite.x = 140;
    sprite.y = 270;
    this.addChild(sprite);

    const inventoryTitle = new Text(LOCALE_DATA[Store.instance.state.locale].NINIMO_INVENTORY, {
      fontSize: Store.instance.state.locale === 'en-US' ? 55 : 90,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      stroke: '0x492A2E',
      strokeThickness: 16,
      lineJoin: 'round',
      padding: 20,
    });
    inventoryTitle.anchor.set(0.5);
    inventoryTitle.x = 540;
    inventoryTitle.y = 630;
    this.addChild(inventoryTitle);

    const balloonChatText = new Text(LOCALE_DATA[Store.instance.state.locale].SELECT_ITEM, {
      fontSize: Store.instance.state.locale === 'en-US' ? 36 : 45,
      fontFamily: 'gaegu',
      fontWeight: '900',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
      letterSpacing: -4,
    });
    balloonChatText.x = 550;
    balloonChatText.y = 312;
    balloonChatText.anchor.set(0.5);
    this.addChild(balloonChatText);

    for (let i = 0; i < 6; i++) {
      const costumeButton = new CostumeItem(i + 1);

      if ((Store.instance.data.costumes as Record<number, boolean>)[i + 1]) {
        costumeButton.interactive = true;
        costumeButton.buttonMode = true;
      }
      costumeButton.x = 415 + (i % 2) * 250;
      costumeButton.y = 840 + Math.floor(i / 2) * 225;
      // eslint-disable-next-line no-loop-func
      costumeButton.on('pointerdown', () => {
        sound.play('/audio/item_touch.mp3', { loop: false, volume: Store.instance.state.volume as number });
        for (const costumeItem of this.costumeItems) {
          costumeItem.selected = false;
        }
        costumeButton.selected = true;
        selectedCostume = i + 1;
      });

      this.addChild(costumeButton);

      this.costumeItems.push(costumeButton);
    }

    const buttonContainer = new Container();
    const buttonTexture = TextureFactory.instance.getSingleTexture('/textures/ui/btn_equip_select.png');
    const equipButton = new Sprite(buttonTexture);

    equipButton.anchor.set(0.5);
    buttonContainer.x = 540;
    buttonContainer.y = 1485;
    buttonContainer.interactive = true;
    buttonContainer.buttonMode = true;

    const text = new Text(LOCALE_DATA[Store.instance.state.locale].EQUIP_DONE, {
      fontSize: 75,
      fontFamily: 'gaegu',
      fontWeight: '900',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
      letterSpacing: -4,
    });
    text.anchor.set(0.5);
    text.y = -5;

    buttonContainer.addChild(equipButton);
    buttonContainer.addChild(text);
    this.addChild(buttonContainer);

    let pressed: boolean = false;

    buttonContainer.on('pointerdown', () => {
      sound.play('/audio/btn.mp3', { loop: false, volume: Store.instance.state.volume as number });
      pressed = true;
      buttonContainer.scale.set(buttonContainer.scale.x * 0.95);
    });
    buttonContainer.on('pointerup', () => {
      if (pressed) {
        buttonContainer.scale.set(buttonContainer.scale.x / 0.95);
        this.emit('equip', selectedCostume);
      }
      pressed = false;
    });
    buttonContainer.on('pointerupoutside', () => {
      pressed = false;
      buttonContainer.scale.set(buttonContainer.scale.x / 0.95);
    });
    logging(LOG_EVENT.GAME_START, LOG_SCENE.BRIDGE_SCENE_3, Store.instance.data.playername, window.navigator.userAgent);
  }

  public update(): void {
    for (const costumeItem of this.costumeItems) {
      costumeItem.update();
    }
  }
}

class ForthCutScene extends Scene {
  private cameraPosition: Point;
  private cutScenes: Array<Cut>;
  private currentCutScene?: Cut;
  private disabled: boolean;
  private inventory: EquipmentsInventory;

  constructor(game: Game) {
    super(game);
    this.cutScenes = [];
    this.cameraPosition = { x: 540, y: 960 };
    this.disabled = false;
    this.createBackground();
    this.createCutScenes();

    this.camera.positionChasingCoefficient = 0.1;
    this.camera.setOffset({ x: 0, y: 0 });
    this.camera.setTargetPosition(this.cameraPosition, true);

    this.inventory = new EquipmentsInventory();
    this.inventory.visible = false;
    this.stage.addChild(this.inventory);
  }

  private createBackground(): void {
    const background = new Graphics();
    background.beginFill(0x000000);
    background.drawRect(0, 0, 5400, 1920);
    background.endFill();

    this.stage.addChild(background);
  }

  private createCutScenes(): void {
    const base = Store.instance.state.locale === 'en-US' ? 'en' : 'ko';
    const cutSpriteInfoArrays: Array<
      Array<{
        audio?: string;
        texture: {
          type: 'IMAGE' | 'VIDEO';
          src: string;
        };
      }>
    > = [
      [
        {
          texture: { type: 'VIDEO', src: `/textures/cut/${base}/cut_4_1.mp4` },
        },
      ],
      [
        {
          audio: '/audio/appear.mp3',
          texture: { type: 'VIDEO', src: `/textures/cut/${base}/cut_4_2.mp4` },
        },
        {
          audio: '/audio/versus.mp3',
          texture: { type: 'IMAGE', src: `/textures/cut/${base}/cut_4_3.jpeg` },
        },
      ],
    ];

    for (let i = 0; i < cutSpriteInfoArrays.length; i++) {
      const cutSpriteInfoArray: Array<{
        audio?: string;
        texture: {
          type: 'IMAGE' | 'VIDEO';
          src: string;
        };
      }> = cutSpriteInfoArrays[i];
      const cut = new Cut(cutSpriteInfoArray, 1);

      cut.x = 540 + (i - 1) * 1080;
      cut.y = 960;

      this.cutScenes.push(cut);
    }

    for (const cutScene of this.cutScenes) {
      cutScene.on('sceneFinished', () => {
        const index = this.cutScenes.indexOf(cutScene);
        if (index < this.cutScenes.length - 1) {
          this.setCutScene(this.cutScenes[index + 1]);
        } else {
          this.currentCutScene = undefined;
          this.popupEquipment();
        }
      });
      this.stage.addChild(cutScene);
    }

    this.setCutScene(this.cutScenes[0]);
  }

  private popupEquipment(): void {
    this.inventory.visible = true;
    this.inventory.once('equip', (costume: number): void => {
      this.inventory.visible = false;

      if ((Store.instance.data.costumes as Record<number, boolean>)[costume]) {
        Store.instance.state.costume = costume;
      }
      this.changeScene();
    });
  }

  protected pointerdown = (_event: InteractionEvent): void => {
    if (
      this.currentCutScene &&
      Math.abs(this.camera.position.x - this.currentCutScene.x) < 10 &&
      Math.abs(this.camera.position.y - this.currentCutScene.y) < 10
    ) {
      this.currentCutScene.next();
    }
  };

  private setCutScene(cutScene: Cut): void {
    this.cameraPosition.x = cutScene.x;
    this.cameraPosition.y = cutScene.y;
    this.currentCutScene = cutScene;
  }

  public changeScene = (): void => {
    const cutaway = new StarCutAway();

    cutaway.setSize(this.game.width, this.game.height);

    this.game.cutaway = cutaway;
    this.game.changeScene(FinalGameScene);
    this.disabled = true;
    for (const cutScene of this.cutScenes) {
      for (const cutSprite of cutScene.cutSprites) {
        if (cutSprite.type === 'VIDEO') {
          (cutSprite.sprite.texture.baseTexture.resource as VideoResource).source.pause();
        }
      }
    }
  };

  public update(elapsed: number): void {
    super.update(elapsed);

    this.inventory.update();
    // Target Cut Update & if End? -> Next Cut Position & Target Change...

    if (!this.disabled) {
      if (this.currentCutScene) {
        this.currentCutScene.update(elapsed);
      }
    }
  }

  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {
    const base = Store.instance.state.locale === 'en-US' ? 'en' : 'ko';
    const assets: Array<string> = [
      '/audio/appear.mp3',
      '/audio/versus.mp3',
      `/textures/cut/${base}/cut_4_3.jpeg`,
      '/textures/ui/btn_equip_select.png',
      '/textures/ui/equipments.png',
      '/textures/ui/frame/black.png',
    ];

    let maxLoaded = 1;
    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
        maxLoaded++;
      }
    }

    let loaded: number = 1;
    return new Promise((resolve) => {
      Loader.shared.onLoad.add(() => {
        callback(++loaded, maxLoaded);
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
    });
  };

  public destroy(): void {
    super.destroy();

    for (const cutScene of this.cutScenes) {
      cutScene.destroy();
    }
  }
}

export default ForthCutScene;
